import { html, css, LitElement } from 'lit-element';
import { repeat } from 'lit-html/directives/repeat.js';
import { db } from './intrivity_app.js'; // Import Firestore instance
import { collection, getDocs, getDoc, doc, query, setDoc, deleteDoc, addDoc, where } from "firebase/firestore";
import uuidv4 from 'v4-uuid';
import {
    emitEvent, loadJSONFromStorage, getJSONFromStorage, getScore, hasRole,
    INTRIVITY_LOAD_RESOURCES, INTRIVITY_LOAD_CLIENTS, INTRIVITY_LOAD_GROUPS,
    INTRIVITY_GROUP_REPORTS_LOADED, INTRIVITY_GOOGLE_LOGGED_IN,
    INTRIVITY_GOOGLE_LOGGED_OUT, INTRIVITY_LOGIN, INTRIVITY_LOGGED_IN,
    INTRIVITY_LOGOUT, INTRIVITY_NAV_SELECT, INTRIVITY_PRINTABLE_TOGGLE,
    INTRIVITY_GROUP_SELECT, INTRIVITY_MASQUERADE
} from './utility.js'

import './intrivity-admin-groups.js';
import './intrivity-user-search.js';
import './intrivity-invitation-template-dialog.js';

// Import necessary MWC components (assuming they are globally available or imported elsewhere)
import '@material/mwc-button';
import '@material/mwc-dialog';
import '@material/mwc-textfield';
import '@material/mwc-icon-button';
import '@material/mwc-list/mwc-list.js';
import '@material/mwc-list/mwc-list-item.js';
import '@material/mwc-snackbar'; // For showing feedback
import '@material/web/all';
import { a11yStyles, disclosureButton, uniqueId } from './a11y.js';
import { t, Localized, LOCALES } from './i18n.js';

// Event to notify parent component of changes
export const INTRIVITY_CLIENTS_UPDATED = 'intrivity-clients-updated';
export const INTRIVITY_CLIENTS_GROUPS_CLOSED = 'intrivity-clients-groups-closed';

export class IntrivityAdminClients extends Localized(LitElement) {

    static get properties() {
        return {
            user_profile: { type: Object },
            clients: { type: Array }, // Passed in from intrivity-app
            _groups: { type: Array }, 
            _isDialogOpen: { type: Boolean, state: true },
            _editingClient: { type: Object, state: true }, // Client being edited (or null for add)
            _clientFormData: { type: Object, state: true }, // Data bound to the form fields
            _isLoading: { type: Boolean, state: true },
            _errorMessage: { type: String, state: true },
            _feedbackMessage: { type: String, state: true },
            _expanded: { type: Boolean, state: true },
            _client: { type: Object, state: true },
            _managersDialogOpen: { type: Boolean, state: true },
            _managersClient: { type: Object, state: true },
            _clientManagers: { type: Array, state: true },
            _managersLoading: { type: Boolean, state: true },
            _templateDialogOpen: { type: Boolean, state: true },
            _templateClient: { type: Object, state: true }
        };
    }

    constructor() {
        super();
        this._panelId = uniqueId('clients');
        this.clients = [];
        this.user_profile = null;
        this._isDialogOpen = false;
        this._editingClient = null;
        this._clientFormData = this._getEmptyClientData();
        this._isLoading = false;
        this._errorMessage = '';
        this._feedbackMessage = '';
        this._managersDialogOpen = false;
        this._managersClient = null;
        this._clientManagers = [];
        this._managersLoading = false;
        this._templateDialogOpen = false;
        this._templateClient = null;

        // Bind methods
        this._openAddDialog = this._openAddDialog.bind(this);
        this._openEditDialog = this._openEditDialog.bind(this);
        this._closeDialog = this._closeDialog.bind(this);
        this._handleFormInput = this._handleFormInput.bind(this);
        this._saveClient = this._saveClient.bind(this);
        this._handleDeleteClient = this._handleDeleteClient.bind(this);
        this._handleCloseGroups = this._handleCloseGroups.bind(this);
        this._openManagersDialog = this._openManagersDialog.bind(this);
        this._closeManagersDialog = this._closeManagersDialog.bind(this);
        this._onManagerUserSelected = this._onManagerUserSelected.bind(this);
    }

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener(INTRIVITY_CLIENTS_GROUPS_CLOSED, this._handleCloseGroups);
    }

    disconnectedCallback() {
        this.removeEventListener(INTRIVITY_CLIENTS_GROUPS_CLOSED, this._handleCloseGroups);    
        super.disconnectedCallback();
    }
    
    _getEmptyClientData() {
        return {
            name: '',
            institution: '',
            email: '',
            address: '',
            payment: 0.00,
            ai_enabled: 0,
            pdf_mode: 'browser',
            uuid: uuidv4() // Will be generated on save for new clients
        };
    }

    _isAdmin() {
        // Basic check - adjust based on your actual role definition
        return hasRole('admin', this.user_profile);
    }

    async _handleCloseGroups() {
        this._client = undefined;
        this.requestUpdate();
    }
    
    _openAddDialog() {
        if (!this._isAdmin()) return;
        this._editingClient = null; // Indicate we are adding a new client
        this._clientFormData = this._getEmptyClientData();
        this._errorMessage = '';
        this._isDialogOpen = true;
    }

    _openEditDialog(client) {
        if (!this._isAdmin()) return;
        this._editingClient = client; // Store the client being edited
        // IMPORTANT: Clone the client data to avoid modifying the original object directly
        this._clientFormData = { ...client };
        this._errorMessage = '';
        this._isDialogOpen = true;
    }

    _closeDialog() {
        this._isDialogOpen = false;
        this._editingClient = null;
        this._clientFormData = this._getEmptyClientData(); // Reset form
        this._errorMessage = '';
    }

    _handleFormInput(event) {
        const target = event.target;
        let { name, value } = target;
        // mwc-select (unlike mwc-textfield) doesn't expose a `name` property,
        // so its fields carry the name via data-name instead.
        if (!name) {
            name = target.dataset.name;
        }
        // Trim whitespace if value is a string
        if (typeof value === 'string') {
            value = value.trim();
        }
        this._clientFormData = {
            ...this._clientFormData,
            [name]: value
        };
        if (name == 'payment') {
            let pValue = value.replace(/[^0-9.]/g, ""); // Remove non-numeric and non-decimal characters
            if (pValue.includes(".")) {
                const parts = pValue.split(".");
                this._clientFormData[name] = Number(parts[0] + "." + parts[1].substring(0, 2)); // Limit to two decimal places
            } else {
                this._clientFormData[name] = Number(pValue + ".00");
            }          
        }
    }

    async _loadClientGroups(client) {
        this._client = client;
        
        //  console.log(`Loading groups for client ID: ${clientId}`);
        //  const groupsRef = collection(db, "groups");
        //  const q = query(groupsRef, where("clientid", "==", clientId));

        //  try {
        //      const snapshot = await getDocs(q);
        //      const _groups = snapshot.docs.map(doc => doc.data());
        //      console.log(_groups);
        //      this._clientId = clientId;
        //      this._groups = _groups;
        //  } catch (error) {
        //      console.error("Error loading groups for " + clientId + ":", error);
        //      return [];
        //  }
    }

    // ---- Client Managers ----

    async _openManagersDialog(client) {
        this._managersClient = client;
        this._managersDialogOpen = true;
        await this._loadClientManagers(client);
    }

    _closeManagersDialog() {
        this._managersDialogOpen = false;
        this._managersClient = null;
        this._clientManagers = [];
    }

    // ---- Invitation Template ----

    _openTemplateDialog(client) {
        this._templateClient = client;
        this._templateDialogOpen = true;
    }

    _closeTemplateDialog() {
        this._templateDialogOpen = false;
        this._templateClient = null;
    }

    async _loadClientManagers(client) {
        this._managersLoading = true;
        try {
            const rolesRef = collection(db, "client_roles");
            const q = query(rolesRef, where("client_uuid", "==", client.uuid));
            const snap = await getDocs(q);
            const managers = await Promise.all(snap.docs.map(async (d) => {
                const data = d.data();
                let displayName = data.email;
                try {
                    const userSnap = await getDoc(doc(db, "users", data.email));
                    if (userSnap.exists()) {
                        const userData = userSnap.data();
                        displayName = userData.displayName || userData.fullName || data.email;
                    }
                } catch (err) {
                    console.warn("Could not load user for client manager row:", err);
                }
                return { id: d.id, email: data.email, displayName };
            }));
            this._clientManagers = managers.sort((a, b) => a.displayName.localeCompare(b.displayName));
        } catch (err) {
            console.error("Error loading client managers:", err);
            this._showError(t('clients.loadManagersFailed'));
        } finally {
            this._managersLoading = false;
        }
        // mwc-dialog caches its scroll/surface layout when opened and doesn't
        // recompute it when content changes while already open, so a newly
        // added/removed manager wouldn't appear until the dialog reopened.
        await this.updateComplete;
        const dialogEl = this.shadowRoot.getElementById('managers-dialog');
        if (dialogEl && typeof dialogEl.forceLayout === 'function') {
            dialogEl.forceLayout();
        }
    }

    // Reconcile 'client_manager' on the user's global role array against all of
    // their client_roles grants, so it's only removed once no client grants it.
    async _reconcileClientManagerRole(email) {
        const rolesRef = collection(db, "client_roles");
        const snap = await getDocs(query(rolesRef, where("email", "==", email)));
        const isClientManager = !snap.empty;

        const userRef = doc(db, "users", email);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) return;
        const userData = userSnap.data();
        const currentRoles = new Set(userData.role || []);

        if (isClientManager && !currentRoles.has('client_manager')) {
            currentRoles.add('client_manager');
        } else if (!isClientManager && currentRoles.has('client_manager')) {
            currentRoles.delete('client_manager');
        } else {
            return;
        }
        await setDoc(userRef, { ...userData, role: Array.from(currentRoles) }, { merge: true });
    }

    async _onManagerUserSelected(e) {
        const user = e.detail && e.detail.user;
        if (!user || !user.email || !this._managersClient) return;

        try {
            const rolesRef = collection(db, "client_roles");
            const existingQ = query(
                rolesRef,
                where("email", "==", user.email),
                where("client_uuid", "==", this._managersClient.uuid)
            );
            const existingSnap = await getDocs(existingQ);
            if (existingSnap.empty) {
                await addDoc(rolesRef, {
                    email: user.email,
                    client_uuid: this._managersClient.uuid,
                    role: ['client_manager'],
                    created_at: new Date().toISOString(),
                });
                await this._reconcileClientManagerRole(user.email);
                this._showFeedback(t('clients.managerAdded', { name: user.displayName || user.email }));
            } else {
                this._showFeedback(t('clients.managerExists', { name: user.displayName || user.email }));
            }
            await this._loadClientManagers(this._managersClient);
        } catch (err) {
            console.error("Error adding client manager:", err);
            this._showError(t('clients.addManagerFailed'));
        }
    }

    async _removeClientManager(manager) {
        if (!this._managersClient) return;
        if (!confirm(t('clients.confirmRemoveManager', { name: manager.displayName, client: this._managersClient.name }))) return;

        try {
            await deleteDoc(doc(db, "client_roles", manager.id));
            await this._reconcileClientManagerRole(manager.email);
            this._showFeedback(t('clients.managerRemoved', { name: manager.displayName }));
            await this._loadClientManagers(this._managersClient);
        } catch (err) {
            console.error("Error removing client manager:", err);
            this._showError(t('clients.removeManagerFailed'));
        }
    }

    async _saveClient() {
        if (!this._isAdmin()) {
            //this._showError("Permission denied.");
            return;
        }
        if (!this._clientFormData.name || !this._clientFormData.institution) {
             alert(this._clientFormData.name);
             this._errorMessage = t('clients.required');
             // Trigger re-render to show error message in dialog
             this.requestUpdate('_errorMessage');
             return;
        }

        this._isLoading = true;
        this._errorMessage = ''; // Clear previous errors

        const clientDataToSave = { ...this._clientFormData };
        let clientId = '';
        let isNew = false;

        if (this._editingClient && this._editingClient.uuid) {
            // Editing existing client
            clientId = this._editingClient.uuid;
            clientDataToSave.uuid = clientId; // Ensure uuid is consistent
        } else {
            // Adding new client
            if (!clientDataToSave.uuid) 
                clientDataToSave.uuid = uuidv4();
            clientId = clientDataToSave.uuid;
            clientDataToSave.created = new Date().toISOString()
            isNew = true;
        }

        try {
            const clientRef = doc(db, "clients", clientId);
            clientDataToSave.last_modified = new Date().toISOString();
            await setDoc(clientRef, clientDataToSave, { merge: true }); // Use setDoc with merge for both add and update

            //this._showFeedback(`Client "${clientDataToSave.name}" ${isNew ? 'added' : 'updated'} successfully.`);
            this._closeDialog();

            // Notify parent component that clients data has changed
            this.dispatchEvent(new CustomEvent(INTRIVITY_CLIENTS_UPDATED, { bubbles: true, composed: true }));

        } catch (error) {
            console.error("Error saving client:", error);
            //this._showError(`Failed to save client: ${error.message}`);
            // Keep dialog open if save failed
            this._errorMessage = t('clients.saveFailed', { message: error.message });
            this.requestUpdate('_errorMessage');

        } finally {
            this._isLoading = false;
        }
    }

    async _handleDeleteClient(client) {
        if (!this._isAdmin()) {
             this._showError(t('clients.permissionDenied'));
             return;
        }
        if (!client || !client.uuid) {
            console.error("Cannot delete client: Invalid data provided.");
            this._showError(t('clients.invalidData'));
            return;
        }

        // Simple confirmation dialog
        if (!confirm(t('clients.confirmDelete', { name: client.name }))) {
            return;
        }

        this._isLoading = true;
        try {
            const clientRef = doc(db, "clients", client.uuid);
            await deleteDoc(clientRef);

            //this._showFeedback(`Client "${client.name}" deleted successfully.`);

            // Notify parent component that clients data has changed
            this.dispatchEvent(new CustomEvent(INTRIVITY_CLIENTS_UPDATED, { bubbles: true, composed: true }));

        } catch (error) {
            console.error("Error deleting client:", error);
            this._showError(t('clients.deleteFailed', { message: error.message }));
        } finally {
            this._isLoading = false;
        }
    }

     // Helper to show snackbar feedback
    _showFeedback(message) {
        this._feedbackMessage = message;
        const snackbar = this.shadowRoot.getElementById('feedback-snackbar');
        if (snackbar) {
            snackbar.labelText = message;
            snackbar.open = true;
        }
    }
     // Helper to show snackbar error
    _showError(message) {
        this._feedbackMessage = message; // Use the same snackbar for errors
        const snackbar = this.shadowRoot.getElementById('feedback-snackbar');
        if (snackbar) {
            snackbar.labelText = message; // Set text directly
            snackbar.open = true;
        }
         console.error(message); // Also log error to console
    }

    static get styles() {
        return [a11yStyles, css`
            :host {
                display: block;
                font-family: sans-serif;
            }
            .client-list {
                margin-top: 20px;
                border: 1px solid #ccc;
                border-radius: 4px;
            }
            .client-item {
                display: flex;
                align-items: center;
                padding: 8px 16px;
                border-bottom: 1px solid #eee;
            }
            .client-item:last-child {
                border-bottom: none;
            }
            .client-details {
                flex-grow: 1;
                margin-right: 16px;
            }
            .client-name {
                font-weight: bold;
            }
            .client-institution {
                font-size: 0.9em;
                color: #555;
            }
             .client-email, .client-address {
                 font-size: 0.85em;
                 color: #6b6b6b;
             }
            .actions {
                display: flex;
                flex-direction: column;
                align-items: flex-end;
                flex: 0 0 auto;
                gap: 4px;
            }
            .actions mwc-button {
                margin-left: 0;
                --mdc-typography-button-font-size: 0.72rem;
                --mdc-button-height: 28px;
            }
            .actions mwc-icon-button {
                color: var(--mdc-theme-primary, #6200ee);
                 --mdc-icon-button-size: 36px; /* Smaller buttons */
                 --mdc-icon-size: 20px;
            }
             .actions mwc-icon-button[icon="delete"] {
                 color: var(--mdc-theme-error, #b00020);
             }
            mwc-dialog {
                --mdc-dialog-min-width: min(90vw, 500px);
            }
            mwc-textfield, mwc-textarea {
                display: block;
                margin-bottom: 16px;
            }
             .dialog-error {
                 color: var(--mdc-theme-error, #b00020);
                 font-size: 0.9em;
                 margin-bottom: 10px;
             }
             mwc-list-item { /* Style list items if using mwc-list */
                display: flex; /* Make sure items are flex containers */
                justify-content: space-between; /* Space out content and actions */
                align-items: center;
                padding: 10px 16px;
             }
             .header-controls {
                 display: flex;
                 justify-content: space-between;
                 align-items: center;
                 margin-bottom: 16px;
             }
             h3 {
                 margin: 0;
             }
             /* Add styles for loading state if needed */
             .loading-indicator {
                 text-align: center;
                 padding: 20px;
                 font-style: italic;
                 color: #666;
             }
             mwc-snackbar {
                /* Position snackbar if needed, default is bottom left */
            }

      /* Was a styled <div>; a real heading gives the admin panels a
         navigable structure (WCAG 1.3.1). */
      .section-header {
        margin: 0 0 5px 0;
        width: 100%;
        box-sizing: border-box;
        color: black;
        font-size: 1.5em;
        font-weight: normal;
        padding: 15px 10px 10px 30px;
        background-color: gray;
      }
      .section-header .disclosure {
        vertical-align: middle;
        margin-left: 8px;
        padding: 4px;
      }
        `];
    }

    render() {
        
      return html`
        <h2 class="section-header">
                ${t('clients.title')}
                ${disclosureButton(this._expanded, t('clients.title'), this._panelId, () => { this._expanded = !this._expanded; })}
        </h2> 
        
        ${this._expanded ? html`

            ${!this._isAdmin() ? html`
                <p>${t('clients.noPermission')}</p>
            `: html`

            ${this._client ? html`
                <intrivity-admin-groups .user_profile=${this.user_profile} .client=${this._client}></intrivity-admin-groups>
            `: html`
        
            <div class="header-controls">
                <h3>${t('clients.manage')}</h3>
                ${hasRole('superadmin', this.user_profile) ? html`
                    <mwc-button
                        raised

                        label=${t('clients.add')}
                        @click=${this._openAddDialog}
                        ?disabled=${this._isLoading}>
                    </mwc-button>
                `: html``}
            </div>

            ${this._isLoading && !this._isDialogOpen ? html`<div class="loading-indicator">${t('common.loading')}</div>` : ''}

            ${!this.clients || this.clients.length === 0 ? html`<p>${t('clients.none')}</p>` : ''}

            <!-- Using mwc-list for better Material styling -->
            <mwc-list class="client-list">
                ${this.clients.sort((a, b) => a.name.localeCompare(b.name)).map(client => html`
                    <mwc-list-item style="display: block; width: auto; height: auto;">
                        <div style="width: 100%; display: flex; flex-wrap: wrap; justify-content: space-between; align-items: flex-start; gap: 8px;">
                            <div style="min-width: 0; flex: 1 1 220px;">
                                <span class="client-name">${client.name}</span>
                                <div class="client-institution">${client.institution || t('clients.noInstitution')}</div>
                                <div class="client-email">${client.email || t('clients.noEmail')}</div>
                                <div class="client-address">${client.address || t('clients.noAddress')}</div>
                                <div class="client-uuid" style="font-size: 0.7em; color: #6b6b6b;">ID: ${client.uuid}</div>
                            </div>
                             <div class="actions">
                                <mwc-button
                                     dense
                                     outlined
                                     label=${t('clients.groups')}
                                     aria-label=${t('groups.groupsOf', { name: client.name })}
                                     @click=${() => this._loadClientGroups(client)}
                                     ?disabled=${this._isLoading}>
                                 </mwc-button>
                                 <mwc-button
                                     dense
                                     aria-label=${t('clients.editNamed', { name: client.name })}
                                     outlined
                                     label=${t('common.edit')}
                                     @click=${() => this._openEditDialog(client)}
                                     ?disabled=${this._isLoading}>
                                 </mwc-button>
                                 <mwc-button
                                     dense
                                     outlined
                                     label=${t('clients.managersButton')}
                                     aria-label=${t('clients.managersFor', { name: client.name })}
                                     @click=${() => this._openManagersDialog(client)}
                                     ?disabled=${this._isLoading}>
                                 </mwc-button>
                                 <mwc-button
                                     dense
                                     outlined
                                     label=${t('groups.editInvitation')}
                                     aria-label=${t('clients.templateFor', { name: client.name })}
                                     @click=${() => this._openTemplateDialog(client)}
                                     ?disabled=${this._isLoading}>
                                 </mwc-button>
                                 ${hasRole('superadmin', this.user_profile) ? html`
                                    <mwc-button
                                        dense
                                        label=${t('common.delete')}
                                        outlined
                                        aria-label=${t('clients.deleteNamed', { name: client.name })}
                                        @click=${() => this._handleDeleteClient(client)}
                                        ?disabled=${this._isLoading}>
                                    </mwc-button>
                                 `: html``}
                             </div>
                         </div>
                    </mwc-list-item>
                    <li divider role="separator"></li>
                `)}
            </mwc-list>

            <!-- Add/Edit Dialog -->
            <mwc-dialog
                heading="${this._editingClient ? t('clients.edit') : t('clients.addNew')}"
                ?open=${this._isDialogOpen}
                @closed=${this._closeDialog}>
                <div>
                    ${this._errorMessage ? html`<div class="dialog-error">${this._errorMessage}</div>` : ''}
                    <mwc-textfield
                        label=${t('clients.nameRequired')}
                        name="name"
                        .value=${this._clientFormData.name}
                        @input=${this._handleFormInput}
                        required
                        ?disabled=${this._isLoading}>
                    </mwc-textfield>
                    <mwc-textfield
                        label=${t('clients.institutionRequired')}
                        name="institution"
                        .value=${this._clientFormData.institution}
                        @input=${this._handleFormInput}
                        required
                        ?disabled=${this._isLoading}>
                    </mwc-textfield>
                    <mwc-textfield
                        label=${t('clients.contactEmail')}
                        name="email"
                        type="email"
                        .value=${this._clientFormData.email}
                        @input=${this._handleFormInput}
                        ?disabled=${this._isLoading}>
                    </mwc-textfield>
                    <mwc-textarea 
                        style="width: 100%"
                        type="textarea"
                        name="address"
                        label=${t('clients.address')} 
                        .value=${this._clientFormData.address || ''}
                        @input=${this._handleFormInput}
                        ?disabled=${this._isLoading}
                    >
                    </mwc-textarea>
                    <mwc-textfield
                        label=${t('clients.defaultPayment')}
                        name="payment"
                        type="text"
                        inputmode="decimal"
                        prefix="$"
                        .value=${this._clientFormData.payment ? (this._clientFormData.payment.toString() || '0.00'): '0.00'}
                        @input=${this._handleFormInput}
                        ?disabled=${this._isLoading}>
                    </mwc-textfield>
                    <mwc-select style="width: 100%;" data-name="ai_enabled" name="ai_enabled" label=${t('field.enableAi')}
                        .value=${this._clientFormData.ai_enabled} @selected=${this._handleFormInput} @closed=${e => e.stopPropagation()}
                        ?disabled=${this._isLoading}>
                        ${[1, 0].map( s => html`<mwc-list-item value=${s} ?selected=${this._clientFormData.ai_enabled == s}>${s==0 ? t('common.false') : t('common.true') }</mwc-list-item>`)}
                    </mwc-select>
                    <mwc-select style="width: 100%;" data-name="pdf_mode" name="pdf_mode" label=${t('clients.pdfMode')}
                        .value=${this._clientFormData.pdf_mode || 'browser'} @selected=${this._handleFormInput} @closed=${e => e.stopPropagation()}
                        ?disabled=${this._isLoading}>
                        <mwc-list-item value="browser" ?selected=${(this._clientFormData.pdf_mode || 'browser') === 'browser'}>${t('clients.pdfBrowser')}</mwc-list-item>
                        <mwc-list-item value="backend" ?selected=${this._clientFormData.pdf_mode === 'backend'}>${t('clients.pdfBackend')}</mwc-list-item>
                    </mwc-select>
                    <label style="display: block; margin: 8px 0;">
                        ${t('invitationLanguage.clientDefaultLabel')}
                        <select @change=${(e) => { this._clientFormData = { ...this._clientFormData, language: e.target.value }; }}>
                            ${Object.keys(LOCALES).map((code) => html`
                                <option value=${code} ?selected=${(this._clientFormData.language || 'en') === code}>${LOCALES[code].name}</option>
                            `)}
                        </select>
                    </label>
                     <!-- Optionally display UUID (read-only) -->
                     ${this._editingClient && this._editingClient.uuid ? html`
                        <mwc-textfield
                            label=${t('clients.clientId')}
                            name="uuid"
                            .value=${this._editingClient.uuid}
                            readonly
                            disabled>
                        </mwc-textfield>
                     ` : 
                     html`
                        <mwc-textfield
                            label=${t('clients.clientId')}
                            name="uuid"
                            .value=${this._clientFormData.uuid || ''}
                            @input=${this._handleFormInput}
                            ?disabled=${this._isLoading}
                            required >
                        </mwc-textfield>
                     `              
             
                }
                </div>
                <mwc-button
                    slot="secondaryAction"
                    dialogAction="cancel"
                    label=${t('common.cancel')}
                    ?disabled=${this._isLoading}>
                </mwc-button>
                <mwc-button
                    slot="primaryAction"
                    raised
                    label="${this._isLoading ? t('common.saving') : t('clients.save')}"
                    @click=${this._saveClient}
                    ?disabled=${this._isLoading}>
                </mwc-button>
            </mwc-dialog>

            <!-- Client Managers Dialog -->
            <mwc-dialog
                id="managers-dialog"
                heading="${this._managersClient ? t('clients.managersFor', { name: this._managersClient.name }) : t('clients.managers')}"
                ?open=${this._managersDialogOpen}
                @closed=${this._closeManagersDialog}>
                <div style="min-width: 320px;">
                    <p style="font-size: 0.85em; color: #666; margin-top: 0;">
                        ${t('clients.managersHelp')}
                    </p>
                    <mwc-button
                        outlined
                        label=${t('clients.addManager')}
                        @click=${() => this.shadowRoot.getElementById('client-manager-search').open()}>
                    </mwc-button>

                    ${this._managersLoading ? html`<div class="loading-indicator">${t('common.loading')}</div>` : ''}

                    ${!this._managersLoading && (!this._clientManagers || this._clientManagers.length === 0) ? html`
                        <p>${t('clients.noManagers')}</p>
                    ` : ''}

                    <mwc-list>
                        ${repeat(this._clientManagers, manager => manager.id, manager => html`
                            <mwc-list-item style="display: block; width: auto; height: auto;">
                                <div style="width: 100%; display: flex; justify-content: space-between; align-items: center;">
                                    <div>
                                        <div>${manager.displayName}</div>
                                        <div style="font-size: 0.8em; color: #6b6b6b;">${manager.email}</div>
                                    </div>
                                    <mwc-button
                                        outlined
                                        label=${t('common.remove')}
                                        @click=${() => this._removeClientManager(manager)}>
                                    </mwc-button>
                                </div>
                            </mwc-list-item>
                        `)}
                    </mwc-list>
                </div>
                <mwc-button slot="primaryAction" dialogAction="close" label=${t('common.close')}></mwc-button>
            </mwc-dialog>
            <intrivity-user-search id="client-manager-search" @user-selected=${this._onManagerUserSelected}></intrivity-user-search>

            <intrivity-invitation-template-dialog
                collection="clients"
                label=${t('clients.client')}
                .target=${this._templateClient}
                .open=${this._templateDialogOpen}
                @dialog-closed=${this._closeTemplateDialog}>
            </intrivity-invitation-template-dialog>

             <!-- Snackbar for feedback -->
            <mwc-snackbar id="feedback-snackbar" labelText="${this._feedbackMessage}"></mwc-snackbar>
        
        
            `}
        
            `}
        `: html`

        `}
        `;
    }
}

customElements.define('intrivity-admin-clients', IntrivityAdminClients);
